/**
 * FaceARViewer.jsx
 * 
 * Face AR viewer using MediaPipe Face Landmarker + Three.js
 * - Front camera video as background
 * - MediaPipe facial transformation matrix for head pose
 * - Three.js 3D scene overlay with PerspectiveCamera
 * - GLB model attached to headAnchor
 * - EMA smoothing for stable tracking
 * - Head occluder mesh for realistic occlusion
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";

// Debug logging
const DEBUG = true;
const log = (...args) => { if (DEBUG) console.log("[FaceAR]", ...args); };

// EMA smoothing factor (0-1, higher = more responsive, lower = smoother)
const SMOOTHING_FACTOR = 0.3;
const POSITION_SMOOTHING = 0.25;

// Face lost grace period (keep last pose for this many ms)
const FACE_LOST_GRACE_MS = 800;

// MediaPipe to Three.js coordinate conversion
// MediaPipe: X right, Y down, Z towards camera
// Three.js: X right, Y up, Z towards viewer
const convertMediaPipeToThreeJS = (matrix4x4) => {
  // Create Three.js Matrix4 from MediaPipe data
  const m = new THREE.Matrix4();
  m.set(
    matrix4x4[0], matrix4x4[1], matrix4x4[2], matrix4x4[3],
    matrix4x4[4], matrix4x4[5], matrix4x4[6], matrix4x4[7],
    matrix4x4[8], matrix4x4[9], matrix4x4[10], matrix4x4[11],
    matrix4x4[12], matrix4x4[13], matrix4x4[14], matrix4x4[15]
  );
  
  // Flip Y and Z axes for Three.js coordinate system
  const flipMatrix = new THREE.Matrix4().makeScale(1, -1, -1);
  m.premultiply(flipMatrix);
  
  return m;
};

const FaceARViewer = ({
  glbUrl,
  projectName,
  faceArTransform = null, // { position, rotation, scale, orientationCorrection }
  onClose,
  debugMode = false,
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  // Three.js refs
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const headAnchorRef = useRef(null);
  const productModelRef = useRef(null);
  const occluderRef = useRef(null);
  
  // MediaPipe refs
  const faceLandmarkerRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  // Smoothing refs
  const smoothedPositionRef = useRef(new THREE.Vector3());
  const smoothedQuaternionRef = useRef(new THREE.Quaternion());
  const lastValidPoseTimeRef = useRef(0);
  const lastValidPoseRef = useRef(null);
  
  // State
  const [status, setStatus] = useState("initializing"); // initializing, loading, ready, tracking, no-face, error
  const [errorMsg, setErrorMsg] = useState("");
  const [modelLoaded, setModelLoaded] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});

  // Initialize MediaPipe Face Landmarker
  const initMediaPipe = useCallback(async () => {
    try {
      log("Initializing MediaPipe Face Landmarker...");
      setStatus("initializing");
      
      const filesetResolver = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
      );
      
      const faceLandmarker = await FaceLandmarker.createFromOptions(filesetResolver, {
        baseOptions: {
          modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
          delegate: "GPU"
        },
        runningMode: "VIDEO",
        numFaces: 1,
        outputFacialTransformationMatrixes: true,
        outputFaceBlendshapes: false,
        minFaceDetectionConfidence: 0.5,
        minFacePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      
      faceLandmarkerRef.current = faceLandmarker;
      log("MediaPipe Face Landmarker initialized");
      return true;
    } catch (err) {
      console.error("MediaPipe init error:", err);
      setErrorMsg(`MediaPipe error: ${err.message}`);
      setStatus("error");
      return false;
    }
  }, []);

  // Initialize camera
  const initCamera = useCallback(async () => {
    try {
      log("Requesting front camera...");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await new Promise((resolve) => {
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play();
            resolve();
          };
        });
        log("Camera initialized:", videoRef.current.videoWidth, "x", videoRef.current.videoHeight);
        return true;
      }
      return false;
    } catch (err) {
      console.error("Camera init error:", err);
      setErrorMsg(`Camera error: ${err.message}`);
      setStatus("error");
      return false;
    }
  }, []);

  // Initialize Three.js scene
  const initThreeJS = useCallback(() => {
    if (!containerRef.current || !videoRef.current) return false;
    
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    
    log("Initializing Three.js scene:", width, "x", height);
    
    // Scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    
    // Camera - PerspectiveCamera matching video FOV
    const fov = 50; // Approximate webcam FOV
    const camera = new THREE.PerspectiveCamera(fov, width / height, 0.01, 1000);
    camera.position.set(0, 0, 0.5); // Camera slightly forward
    cameraRef.current = camera;
    
    // Renderer with alpha for video background
    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      alpha: true,
      antialias: true,
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    rendererRef.current = renderer;
    
    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(0, 1, 1);
    scene.add(directionalLight);
    
    // Head anchor - all face-attached objects are children of this
    const headAnchor = new THREE.Group();
    headAnchor.name = "headAnchor";
    scene.add(headAnchor);
    headAnchorRef.current = headAnchor;
    
    // Create head occluder (invisible mesh that hides parts of model behind head)
    const occluderGeometry = new THREE.SphereGeometry(0.09, 32, 32);
    const occluderMaterial = new THREE.MeshBasicMaterial({
      colorWrite: false,
      depthWrite: true,
    });
    const occluder = new THREE.Mesh(occluderGeometry, occluderMaterial);
    occluder.position.set(0, 0, 0.02); // Slightly forward from head center
    occluder.renderOrder = -1; // Render first
    headAnchor.add(occluder);
    occluderRef.current = occluder;
    
    log("Three.js scene initialized");
    return true;
  }, []);

  // Load GLB model
  const loadModel = useCallback(async () => {
    if (!glbUrl || !headAnchorRef.current) return false;
    
    try {
      log("Loading GLB model:", glbUrl);
      setStatus("loading");
      
      const loader = new GLTFLoader();
      
      return new Promise((resolve, reject) => {
        loader.load(
          glbUrl,
          (gltf) => {
            const model = gltf.scene;
            
            // Create container for transforms
            const container = new THREE.Group();
            container.name = "productContainer";
            container.add(model);
            
            // Apply saved transform if available
            if (faceArTransform) {
              log("Applying saved transform:", faceArTransform);
              
              // Apply orientation correction first (to the model itself)
              if (faceArTransform.orientationCorrection) {
                const oc = faceArTransform.orientationCorrection;
                model.rotation.set(
                  THREE.MathUtils.degToRad(oc.x || 0),
                  THREE.MathUtils.degToRad(oc.y || 0),
                  THREE.MathUtils.degToRad(oc.z || 0)
                );
              }
              
              // Apply local transform to container (relative to head anchor)
              if (faceArTransform.position) {
                container.position.set(
                  faceArTransform.position.x || 0,
                  faceArTransform.position.y || 0,
                  faceArTransform.position.z || 0
                );
              }
              if (faceArTransform.rotation) {
                container.rotation.set(
                  THREE.MathUtils.degToRad(faceArTransform.rotation.x || 0),
                  THREE.MathUtils.degToRad(faceArTransform.rotation.y || 0),
                  THREE.MathUtils.degToRad(faceArTransform.rotation.z || 0)
                );
              }
              if (faceArTransform.scale) {
                const s = faceArTransform.scale;
                container.scale.set(s.x || 1, s.y || 1, s.z || 1);
              }
            } else {
              // Default position for glasses (on nose bridge)
              container.position.set(0, 0.02, 0.08);
              container.scale.set(0.1, 0.1, 0.1);
            }
            
            // Add to head anchor
            headAnchorRef.current.add(container);
            productModelRef.current = container;
            
            setModelLoaded(true);
            setStatus("ready");
            log("Model loaded and attached to head anchor");
            resolve(true);
          },
          (progress) => {
            if (progress.total > 0) {
              const pct = Math.round((progress.loaded / progress.total) * 100);
              log("Loading progress:", pct, "%");
            }
          },
          (error) => {
            console.error("Model load error:", error);
            reject(error);
          }
        );
      });
    } catch (err) {
      console.error("Model load error:", err);
      setErrorMsg(`Model error: ${err.message}`);
      setStatus("error");
      return false;
    }
  }, [glbUrl, faceArTransform]);

  // EMA smoothing helper
  const smoothValue = (current, target, factor) => {
    return current + (target - current) * factor;
  };

  // Main render loop
  const renderLoop = useCallback(() => {
    if (!faceLandmarkerRef.current || !videoRef.current || !rendererRef.current) {
      animationFrameRef.current = requestAnimationFrame(renderLoop);
      return;
    }
    
    const video = videoRef.current;
    const faceLandmarker = faceLandmarkerRef.current;
    const headAnchor = headAnchorRef.current;
    const renderer = rendererRef.current;
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    
    if (video.readyState >= 2) {
      const now = performance.now();
      
      // Run face detection
      const results = faceLandmarker.detectForVideo(video, now);
      
      if (results.facialTransformationMatrixes && results.facialTransformationMatrixes.length > 0) {
        // Face detected
        const matrixData = results.facialTransformationMatrixes[0].data;
        
        // Convert to Three.js coordinate system
        const threeMatrix = convertMediaPipeToThreeJS(matrixData);
        
        // Decompose matrix
        const position = new THREE.Vector3();
        const quaternion = new THREE.Quaternion();
        const scale = new THREE.Vector3();
        threeMatrix.decompose(position, quaternion, scale);
        
        // Scale position to match scene scale (MediaPipe uses meters)
        position.multiplyScalar(0.5);
        
        // Apply EMA smoothing
        smoothedPositionRef.current.x = smoothValue(smoothedPositionRef.current.x, position.x, POSITION_SMOOTHING);
        smoothedPositionRef.current.y = smoothValue(smoothedPositionRef.current.y, position.y, POSITION_SMOOTHING);
        smoothedPositionRef.current.z = smoothValue(smoothedPositionRef.current.z, position.z, POSITION_SMOOTHING);
        
        smoothedQuaternionRef.current.slerp(quaternion, SMOOTHING_FACTOR);
        
        // Apply to head anchor
        if (headAnchor) {
          headAnchor.position.copy(smoothedPositionRef.current);
          headAnchor.quaternion.copy(smoothedQuaternionRef.current);
          headAnchor.visible = true;
        }
        
        // Update state
        lastValidPoseTimeRef.current = now;
        lastValidPoseRef.current = {
          position: smoothedPositionRef.current.clone(),
          quaternion: smoothedQuaternionRef.current.clone(),
        };
        
        if (!faceDetected) {
          setFaceDetected(true);
          setStatus("tracking");
        }
        
        // Update debug info
        if (debugMode) {
          setDebugInfo({
            position: `(${position.x.toFixed(3)}, ${position.y.toFixed(3)}, ${position.z.toFixed(3)})`,
            smoothedPos: `(${smoothedPositionRef.current.x.toFixed(3)}, ${smoothedPositionRef.current.y.toFixed(3)}, ${smoothedPositionRef.current.z.toFixed(3)})`,
            rotation: `(${THREE.MathUtils.radToDeg(quaternion.x).toFixed(1)}, ${THREE.MathUtils.radToDeg(quaternion.y).toFixed(1)}, ${THREE.MathUtils.radToDeg(quaternion.z).toFixed(1)})`,
            tracking: "active",
          });
        }
      } else {
        // No face detected - use grace period
        const timeSinceLastFace = now - lastValidPoseTimeRef.current;
        
        if (timeSinceLastFace < FACE_LOST_GRACE_MS && lastValidPoseRef.current) {
          // Keep last valid pose during grace period
          if (headAnchor) {
            headAnchor.visible = true;
          }
          
          if (debugMode) {
            setDebugInfo(prev => ({
              ...prev,
              tracking: `grace (${Math.round(FACE_LOST_GRACE_MS - timeSinceLastFace)}ms)`,
            }));
          }
        } else {
          // Grace period expired - hide model
          if (headAnchor) {
            headAnchor.visible = false;
          }
          
          if (faceDetected) {
            setFaceDetected(false);
            setStatus("no-face");
          }
          
          if (debugMode) {
            setDebugInfo(prev => ({
              ...prev,
              tracking: "lost",
            }));
          }
        }
      }
      
      // Render
      if (renderer && scene && camera) {
        renderer.render(scene, camera);
      }
    }
    
    animationFrameRef.current = requestAnimationFrame(renderLoop);
  }, [faceDetected, debugMode]);

  // Initialize everything
  useEffect(() => {
    let mounted = true;
    
    const init = async () => {
      // Initialize in sequence
      const mpOk = await initMediaPipe();
      if (!mounted || !mpOk) return;
      
      const camOk = await initCamera();
      if (!mounted || !camOk) return;
      
      // Wait a bit for video to stabilize
      await new Promise(r => setTimeout(r, 100));
      
      const threeOk = initThreeJS();
      if (!mounted || !threeOk) return;
      
      const modelOk = await loadModel();
      if (!mounted || !modelOk) return;
      
      // Start render loop
      renderLoop();
    };
    
    init();
    
    return () => {
      mounted = false;
      
      // Cleanup
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      }
      
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
      
      if (faceLandmarkerRef.current) {
        faceLandmarkerRef.current.close();
      }
    };
  }, [initMediaPipe, initCamera, initThreeJS, loadModel, renderLoop]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;
      
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(width, height);
    };
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div 
      ref={containerRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "#000",
        overflow: "hidden",
      }}
    >
      {/* Video background (mirrored for selfie view) */}
      <video
        ref={videoRef}
        playsInline
        muted
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: "scaleX(-1)", // Mirror for selfie
        }}
      />
      
      {/* Three.js canvas overlay */}
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          transform: "scaleX(-1)", // Mirror to match video
          pointerEvents: "none",
        }}
      />
      
      {/* UI Overlay */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
        display: "flex",
        flexDirection: "column",
      }}>
        {/* Top bar */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px",
          pointerEvents: "auto",
        }}>
          <h2 style={{ color: "#fff", margin: 0, fontSize: 18, textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>
            {projectName || "Face AR"}
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: "rgba(0,0,0,0.5)",
                border: "none",
                color: "#fff",
                padding: "8px 16px",
                borderRadius: 20,
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Zamknij
            </button>
          )}
        </div>
        
        {/* Center status */}
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {status === "initializing" && (
            <div style={{ color: "#fff", textAlign: "center" }}>
              <div style={{ marginBottom: 8 }}>Inicjalizacja...</div>
            </div>
          )}
          {status === "loading" && (
            <div style={{ color: "#fff", textAlign: "center" }}>
              <div style={{ marginBottom: 8 }}>Ładowanie modelu...</div>
            </div>
          )}
          {status === "ready" && !faceDetected && (
            <div style={{ color: "#fff", textAlign: "center", background: "rgba(0,0,0,0.5)", padding: "16px 24px", borderRadius: 12 }}>
              <div style={{ marginBottom: 8 }}>Szukam twarzy...</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>Skieruj kamerę na twarz</div>
            </div>
          )}
          {status === "no-face" && (
            <div style={{ color: "#fff", textAlign: "center", background: "rgba(0,0,0,0.5)", padding: "16px 24px", borderRadius: 12 }}>
              <div style={{ marginBottom: 8 }}>Twarz zgubiona</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>Wróć do kadru</div>
            </div>
          )}
          {status === "error" && (
            <div style={{ color: "#f55", textAlign: "center", background: "rgba(0,0,0,0.7)", padding: "16px 24px", borderRadius: 12 }}>
              <div style={{ marginBottom: 8 }}>Błąd</div>
              <div style={{ fontSize: 12 }}>{errorMsg}</div>
            </div>
          )}
        </div>
        
        {/* Debug panel */}
        {debugMode && (
          <div style={{
            position: "absolute",
            bottom: 80,
            left: 10,
            background: "rgba(0,0,0,0.8)",
            color: "#0f0",
            padding: 10,
            borderRadius: 8,
            fontSize: 10,
            fontFamily: "monospace",
            pointerEvents: "none",
          }}>
            <div style={{ color: "#fff", fontWeight: "bold", marginBottom: 4 }}>Face AR Debug</div>
            <div>status: {status}</div>
            <div>faceDetected: {faceDetected ? "yes" : "no"}</div>
            <div>modelLoaded: {modelLoaded ? "yes" : "no"}</div>
            <div>tracking: {debugInfo.tracking || "-"}</div>
            <div>position: {debugInfo.position || "-"}</div>
            <div>smoothedPos: {debugInfo.smoothedPos || "-"}</div>
            <div>rotation: {debugInfo.rotation || "-"}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FaceARViewer;
